import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ForgotPasswordHeader } from '@/components/auth/ForgotPasswordHeader';
import { RequestResetForm } from '@/components/auth/RequestResetForm';
import { ForgotPasswordFooter } from '@/components/auth/ForgotPasswordFooter';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const ForgotPassword: React.FC = () => {
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Cooldown timer effect
  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (cooldownSeconds > 0) {
      timer = setTimeout(() => {
        setCooldownSeconds(prev => prev - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  const handleReset = async (userEmail: string) => {
    if (cooldownSeconds > 0) {
      toast({
        title: "Please wait",
        description: `You can request another reset email in ${cooldownSeconds} seconds.`,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      console.log('Sending password reset email to:', userEmail);
      

      //use supabase auth resetPasswordForEmail
      // const { data:emailData , error:emailError } = await supabase.auth.signInWithOtp({
      //   email:    userEmail  ,
      //   options: {
      //     emailRedirectTo: `${window.location.origin}/reset-password`
      //   }
      // });
      const { data:emailData , error:emailError } = await supabase.auth.resetPasswordForEmail(userEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      });
 
      
      if (emailError) {
        console.error('Password reset error:', emailError);
        throw new Error(emailError.message || 'Failed to send reset email');
      }

      if (!emailData) {
        throw new Error('Failed to send reset email');
      }
      
      console.log('Password reset email sent successfully to:', userEmail);
      setEmail(userEmail);
      setIsEmailSent(true);
      setCooldownSeconds(60); // Set 60 second cooldown after successful send
      
      toast({
        title: "Password reset email sent",
        description: `Check your email for a password reset link from ProptPilot.`,
      });
    } catch (error) {
      console.error("Error sending password reset email:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send reset email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeEmail = () => {
    setIsEmailSent(false);
    setEmail('');
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-background p-4">
      <div className="w-full max-w-md space-y-8">
        <ForgotPasswordHeader emailSent={isEmailSent} />
        
        {isEmailSent ? (
          <div className="text-center space-y-4">
            <p className="text-muted-foreground">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <p className="text-sm text-muted-foreground">
              Click the link in your email to reset your password. The email may take a few minutes to arrive.
            </p>
            {cooldownSeconds > 0 && (
              <div className="bg-yellow-50 p-3 rounded-md">
                <p className="text-sm text-yellow-800">
                  You can request another reset email in <strong>{cooldownSeconds}</strong> seconds.
                </p>
              </div>
            )}
            <div className="flex flex-col space-y-2">
              <button 
                type="button" 
                onClick={handleChangeEmail}
                className="text-primary hover:underline text-sm"
                disabled={isLoading}
              >
                Use a different email address
              </button>
              <Link 
                to="/login" 
                className="text-primary hover:underline text-sm"
              >
                Back to login
              </Link>
            </div>
          </div>
        ) : (
          <>
            <RequestResetForm 
              onReset={handleReset} 
              isLoading={isLoading || cooldownSeconds > 0} 
            />
            {cooldownSeconds > 0 && (
              <div className="bg-yellow-50 p-3 rounded-md text-center">
                <p className="text-sm text-yellow-800">
                  Please wait <strong>{cooldownSeconds}</strong> seconds before requesting another reset email.
                </p>
              </div>
            )}
          </>
        )}
        
        <ForgotPasswordFooter />
      </div>
    </div>
  );
};

export default ForgotPassword;
